export type CampaignStats = {
    plays: number;
    plays_expected: number;
    plays_percentage: number;
    plays_in_schedule: number;
    plays_out_schedule: number;
    plays_unbooked: number;

    impacts: number;
    impacts_expected: number;
    impacts_percentage: number;
    impacts_in_schedule: number;
    impacts_out_schedule: number;
    impacts_unbooked: number;

    geopath_audience: number;
    geopath_sov: number;

    time: number;
    time_expected: number;
    time_percentage: number;
    time_in_schedule: number;
    time_out_schedule: number;
    time_unbooked: number;

    kilowatts: number;
    kgco2: number;
};

export type PCAReportRequestPayload = {
    campaignId: number;
    startDate: string;
    endDate: string;
    metric?: PCAReportMetric;
    campaignStats?: boolean;
    timeFrame?: PCAReportTimeFrame;
    field?: PCAReportField;
    environment?: string;
    source?: 'es' | 'athena';

    fieldFilters?: Partial<Record<PCAReportField, any[]>>;

    /**
     * Defaults to all
     */
    countFields?: PCAReportCountField[];
};

export enum PCAReportEnvironment {
    Live = 'live',
    Sandbox = 'sandbox',
}

export enum PCAReportTimeFrame {
    Hourly = 'Hourly',
    Daily = 'Daily',
}

export enum PCAReportCountField {
    InSchedule = 'in_schedule',
    OutSchedule = 'out_schedule',
    UnBooked = 'unbooked',
}

export enum PCAReportMetric {
    Plays = 'plays',
    Impacts = 'impacts',
    Time = 'time',
    Frames = 'frames',
    Display = 'display',
}

export enum PCAReportField {
    Town = 'town',
    TvRegion = 'tvRegion',
    Conurbation = 'conurbation',
    Environment = 'environment',
    Format = 'format',
    MediaOwner = 'mediaOwner',
    FrameId = 'frameId',
    UnitId = 'unitId',
    LineItemName = 'lineItemName',
    SurfaceArea = 'surfaceArea',
    Resolution = 'resolution',
    CreativeName = 'creativeName',
    DayOfWeek = 'dayOfWeek',
    Hour = 'hour',
    DataStatus = 'data_status',
}

export type PCAReportPlayStats = [
    actual: number,
    expected: number,
    percentage: number,
    in_schedule: number,
    out_schedule: number,
    unbooked: number,
];

export type PCAReportByFieldPlayStats = [
    actual: number,
    expected: number,
    percentage: number,
    in_schedule: number,
    out_schedule: number,
    unbooked: number,
    resourceId?: string | number,
    display_count?: number | null,
];

export type PCAReportHourlyResponse = {
    campaignStats?: CampaignStats;

    /**
     * The key of the top level object will be a date, for example, '01-12-2021'
     * The key at the second level will be a the hour (0 - 23)
     *
     * Example:
     * const example = {
     *    reportData: {
     *        '2021-06-15': {
     *            '0': [0, 2925, '0.0000', 123, 1234, 1],
     *            ...
     *            '23': [1678, 4185, '40.0956'm 123, 1234, 1],
     *        },
     *    },
     * };
     */
    reportData: Record<string, Record<string, PCAReportPlayStats | CampaignStats>>;
};

export type PCAReportDailyResponse = {
    campaignStats?: CampaignStats;

    /**
     * The key will be a date, for example, '01-12-2021'
     *
     * Example:
     *  const example = {
     *     reportData: {
     *         '2021-06-15': [242075, 295691, '81.8676', 123, 1234, 1],
     *     },
     *  };
     */
    reportData: Record<string, PCAReportPlayStats>;
};

export type PCAReportByFieldResponse = {
    campaignStats?: CampaignStats;

    /**
     * The key could be a town name (if the Town field is chosen), for example, 'London'
     *
     * Example:
     * const example = {
     *    reportData: {
     *        'London': [242075, 295691, '81.8676', 123, 1234, 1],
     *    },
     * };
     */
    reportData: Record<string, PCAReportByFieldPlayStats>;
};

export type PCAReportEnergyStats = {
    kilowatts: string; // decimal
    kgco2: string; // decimal
    sum_of_plays: number;
    sum_of_time: number;
};
export type PCAReportEnergyByFieldResponse = {
    campaignStats?: CampaignStats;

    reportData: Record<string, PCAReportEnergyStats>;
};

export type PCAReportByFieldDailyResponse = {
    campaignStats?: CampaignStats;
    reportData: Record<string, PCAReportDailyResponse['reportData']>;
};

export type PCAReportByFieldHourlyResponse = {
    campaignStats?: CampaignStats;
    reportData: Record<string, PCAReportHourlyResponse['reportData']>;
};

/**
 * MAP
 */

export type PCAMapReportRequestPayload = {
    campaignId: number;
    startDate: string;
    endDate: string;
    metric: PCAReportMetric;
    campaignStats?: boolean;
    environment?: string;
    source?: 'es' | 'athena';

    /**
     * Defaults to all
     */
    countFields?: PCAReportCountField[];

    fieldFilters?: Partial<Record<PCAReportField, any[]>>;
};

export type PCAMapReportResponse = {
    campaignStats?: CampaignStats;

    /**
     * The key could is the frame id, for example, '1234627105'
     *
     * Example:
     * const example = {
     *     reportData: {
     *         "1234627105": [
     *             0,
     *             15120,
     *             "0.0000",
     *
     *             123,
     *             1234,
     *             1,
     *
     *             "51.54158",
     *             "-0.00438",
     *
     *             "Stratford West Ham",
     *             "Greater London",
     *             "1.35",
     *             "Greater London",
     *             "Transport",
     *             "D6",
     *             "Stratford Station, Station Street, Stratford, Greater London\r",
     *             "Global"
     *             "1920x1080",
     *         ],
     *     },
     * };
     */
    reportData: Record<string, PCAMapReportPlayStats>;
};

export type PCAMapReportPlayStats = [
    actual: number,
    expected: number,
    percentage: string,

    inSchedule: number,
    outSchedule: number,
    unbooked: number,

    latitude: string,
    longitude: string,

    town: string,
    tvRegion: string,
    surfaceArea: string,
    conurbation: string,
    environment: string,
    format: string,
    lineItemName: string,
    mediaOwner: string,
    resolution: string,
    display_id: string,
];

export type PCAMapReportResponseMinimal = {
    campaignStats?: CampaignStats;

    /**
     * The key could is the frame id, for example, '1234627105'
     *
     * Example:
     * const example = {
     *   "data": {
     *      "1234625525": [
     *         5519,
     *         53.4043,
     *         -2.9793,
     *         0,
     *         90,
     *         0,
     *         0,
     *         0,
     *         0,
     *         0,
     *         0,
     *         0,
     *         0,
     *         0,
     *         540,
     *         0,
     *         0,
     *         0
     *      ],
     *      "1234626091": [
     *         5533,
     *         53.4083,
     *         -2.9797,
     *         0,
     *         90,
     *         0,
     *         0,
     *         0,
     *         0,
     *         0,
     *         0,
     *         0,
     *         0,
     *         0,
     *         540,
     *         0,
     *         0,
     *         0
     *     ]
     *   }
     * }
     */
    reportData: Record<string, PCAMapReportPlayStatsMinimal>;
};
export type PCAMapReportPlayStatsMinimal = [
    display_id: number,
    display_lat: number,
    display_long: number,
    sum_of_play: number,
    expected_plays_in_schedule: number,
    plays_received_in_schedule: number,
    plays_received_out_of_schedule: number,
    plays_received_unbooked: number,
    expected_impacts_in_schedule: number,
    audience: number,
    impacts_received_in_schedule: number,
    impacts_received_out_of_schedule: number,
    impacts_received_unbooked: number,
    sum_of_time: number,
    expected_time_in_schedule: number,
    time_received_in_schedule: number,
    time_received_out_of_schedule: number,
    time_received_unbooked: number,
];

export type PCACampaignFramesRequestPayload = {
    campaignId: number;
    metric: PCAReportMetric;
    fieldFilters?: Partial<Record<PCAReportField, any[]>>;
    startDate?: string;
    endDate?: string;
    environment?: string;
};

export type PCAFrameDetails = {
    latitude: number;
    longitude: number;
    displayId?: number;

    town: string;
    tvRegion: string;
    surfaceArea: string;
    conurbation: string;
    environment: string;
    format: string;
    lineItemName: string;
    mediaOwner: string;
    resolution: string;

    address: string;
    postcode: string;
    frameId: string;
    unitId?: string;

    actual: number;
    expected: number;
    percentage: number;

    in_schedule: number;
    out_schedule: number;
    unbooked: number;
    unbookedBool?: boolean;
};

/**
 * Analytics
 */

export type PCAAnalyticsReportRequestPayload = {
    campaignId: number;
    startDate: string;
    endDate: string;
    environment?: string;

    /**
     * Defaults to all
     */
    countFields?: PCAReportCountField[];

    fieldFilters?: Partial<Record<PCAReportField, any[]>>;
};

export type PCAAnalyticsReportResponse = {
    campaignStats?: CampaignStats;

    dailyImpacts: Record<string, PCAReportPlayStats>;
    dailyPlays: Record<string, PCAReportPlayStats>;

    dayOfWeek: Record<string, PCAReportPlayStats>;
    tvRegion: Record<string, PCAReportPlayStats>;
    lineItemName: Record<string, PCAReportPlayStats>;
    mediaOwner: Record<string, PCAReportPlayStats>;
    hour: Record<string, PCAReportPlayStats>;
};

export type DashboardReportFields = {
    id?: string;
    name?: string;
    reference?: string;
    buyer?: string;
    agency?: string;
    client?: string;
    scheduled?: string;
    frame_id?: string;
    inside_campaign_settings_date?: string;
    playout_start_date?: string;
    playout_start_time?: string;
    playout_start_datetime?: string;
    playout_end_date?: string;
    playout_end_time?: string;
    playout_end_datetime?: string;
    playout_day_of_week?: string;
    creative_id?: string;
    creative_name?: string;
    sum_of_play?: number;
    expected_plays?: number;
    sum_of_creatives?: number;
    sum_of_time?: number;
    sov?: number;
    slot_duration?: number;
    expected_time?: number;
    audience_sov?: number;
    audience?: number;
    playout_percentage_plays?: number;
    playout_percentage_time?: number;
    expected_audience?: number;
    audience_received?: number;
    audience_received_percent?: number;
    expected_impacts_in_schedule?: number;
    impacts_received_in_schedule?: number;
    impacts_received_out_of_schedule?: number;
    impacts_received_unbooked?: number;
    expected_plays_in_schedule?: number;
    plays_received_in_schedule?: number;
    plays_received_out_of_schedule?: number;
    plays_received_unbooked?: number;
    expected_time_in_schedule?: number;
    time_received_in_schedule?: number;
    time_received_out_of_schedule?: number;
    time_received_unbooked?: number;
    day?: string;
    hour?: string;
    display_id?: string;
    issue_id?: string;
    downtime?: string;
    plays_with_verification?: number;
    time_with_verification?: number;
    impacts_with_verification?: number;
    energy_uses_kwh?: number;
    carbon_emission_kgs_co2e?: number;
    display_sov?: number;
    display_slots?: number;
    display_slot_duration?: number;
    display_town?: string;
    display_tv_region?: string;
    display_conurbation?: string;
    display_type?: string;
    display_resolution?: string;
    display_filetypes?: string;
    display_env?: string;
    display_surface_area?: string;
    display_lat?: string;
    display_long?: string;
    display_format?: string;
    display_line_item_id?: string;
    display_line_item_name?: string;
    display_owner?: string;
    display_owner_id?: string;
    display_screen_name_summary?: string;
    unit_id?: string;
    playout_start_timestamp?: number;
    playout_start_hour?: number;
    playout_end_timestamp?: number;
    playout_end_hour?: number;
    environment?: string;
    campaign_id?: string;
    expected_impacts_in_schedule_sum?: number;
    impacts_received_in_schedule_sum?: number;
    impacts_received_out_of_schedule_sum?: number;
    impacts_received_unbooked_sum?: number;
}[];

/**
 * A booked classic line: on the campaign's schedule, whether or not it has CAD rows yet
 * (GET reporting/pcareport/:id/classic-booked-lines). Delivery and poster-up dates come from the CAD.
 */
export type ClassicBookedLine = {
    display_id: number;
    frame_id: string | null;
    name: string | null;
    address: string | null;
    postcode: string | null;
    /** Market (DMA). */
    town: string | null;
    /** State. */
    tv_region: string | null;
    conurbation: string | null;
    format: string | null;
    owner_id: number | null;
    owner_name: string | null;
    /** yyyy-MM-dd */
    booked_start: string | null;
    /** Where booked_start came from, most precise first. */
    booked_start_source: 'schedule' | 'line_item' | 'campaign';
};

// ---- Datasources (saved Athena tables) — /reporting/datasource/:id/{fields,query} ----
export type DatasourceListItem = {
    id: number;
    name: string;
    slug: string;
    description: string | null;
    is_queryable: boolean;
    is_admin_only: boolean;
    column_schema: Record<string, string> | null;
    last_synced_at: string | null;
    row_count: number;
};

export type DatasourceField = { name: string; type: string; is_partition: boolean };

export type DatasourceFieldsResponse = {
    id: number;
    slug: string;
    is_queryable: boolean;
    last_synced_at: string | null;
    row_count: number;
    fields: DatasourceField[];
};

// Full datasource definition (authoring) — a superset of the list row.
export type DatasourceDetail = DatasourceListItem & {
    query: string;
    source_connection: string;
    interval: string;
    next_run_at: string | null;
    last_run_at: string | null;
    is_paused: boolean;
    paused_at: string | null;
    partition_columns: string[];
    scope_campaign_column: string | null;
    scope_owner_column: string | null;
    active_location: string | null;
    created_at?: string;
    updated_at?: string;
    syncs?: DatasourceSyncRun[];
};

export type DatasourceCreatePayload = {
    name: string;
    description?: string | null;
    slug: string;
    query: string;
    source_connection?: string;
    interval: string;
    is_paused?: boolean;
    partition_columns?: string[];
    scope_campaign_column?: string | null;
    scope_owner_column?: string | null;
};

// PUT accepts everything create does except the slug.
export type DatasourceUpdatePayload = Partial<Omit<DatasourceCreatePayload, 'slug'>>;

export type DatasourceColumn = { name: string; type: string };

export type DatasourceDetectColumnsResponse = {
    success: boolean;
    columns?: DatasourceColumn[];
    message?: string;
};

export type DatasourceSyncRun = {
    id: number;
    datasource_id: number;
    run_type: 'manual' | 'scheduled';
    status: 'running' | 'succeeded' | 'failed';
    started_at: string | null;
    completed_at: string | null;
    row_count: number | null;
    bytes_scanned: number | null;
    error_message: string | null;
    created_at?: string;
    updated_at?: string;
};
